import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AXLE, SeasonBadge, fd } from "@/components/tyres/common";
import { api, errMsg } from "@/lib/api";

const Change = ({ c, canDelete, onDelete }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={`tyre-history-row-${c.id}`}>
    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
      <span className="font-semibold text-slate-900">{fd(c.date)}</span>
      <span className="rounded-full bg-slate-900 px-2 py-0.5 font-semibold text-white">{AXLE[c.axle]} · {c.qty} gomme</span>
      {c.km != null && <span className="text-slate-500">{c.km.toLocaleString("it-IT")} km</span>}
      {c.shop_name && <span className="text-slate-500">· {c.shop_name}</span>}
      {c.order_number && <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-800">Ordine {c.order_number}</span>}
      {canDelete && !c.order_id && <Button size="sm" variant="ghost" className="ml-auto h-6 text-slate-400" onClick={() => onDelete(c)} data-testid={`tyre-history-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
    </div>
    <div className="grid items-center gap-2 text-xs sm:grid-cols-[1fr_auto_1fr]">
      <div className="rounded-md bg-slate-50 p-2"><p className="text-[10px] uppercase tracking-wider text-slate-400">Precedenti</p>
        <p className="font-medium">{c.old_rear_type_label && "Ant.: "}{c.old_type_label ? c.old_type_label.split(" · ")[0] : "Non note"}</p><SeasonBadge season={c.old_season} />
        {c.old_rear_type_label && <><p className="mt-1 font-medium">Post.: {c.old_rear_type_label.split(" · ")[0]}</p><SeasonBadge season={c.old_rear_season} /></>}
        {(c.old_type_label || c.old_rear_type_label) && <p className="mt-1 text-slate-500">{c.removed_to === "MAGAZZINO" ? "Tornate a magazzino" : "Smaltite"}</p>}</div>
      <ArrowRight className="mx-auto hidden h-4 w-4 text-slate-400 sm:block" />
      <div className="rounded-md bg-emerald-50 p-2"><p className="text-[10px] uppercase tracking-wider text-emerald-700">Attuali</p>
        <p className="font-medium">{c.new_type_label.split(" · ")[0]}</p><SeasonBadge season={c.new_season} />
        <p className="mt-1 text-slate-500">{c.new_condition === "NUOVA" ? "Nuove" : "Usate"}</p></div>
    </div>
    {c.notes && <p className="mt-2 text-xs text-slate-600">{c.notes}</p>}
  </div>
);

export const HistoryDialog = ({ vehicle, sid, isAdmin, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/tyres/changes", { params: { station_id: isAdmin ? sid : undefined, vehicle_id: vehicle.id } }).then((r) => setRows(r.data));
  }, [sid, isAdmin, vehicle.id]);
  useEffect(() => { load(); }, [load]);
  const remove = async (c) => {
    if (!window.confirm("Eliminare questo cambio? Le quantità a magazzino vengono ripristinate.")) return;
    try { await api.delete(`/tyres/changes/${c.id}`); toast.success("Cambio eliminato"); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="tyre-history-dialog">
        <DialogHeader>
          <DialogTitle>Storico gomme · {vehicle.plate}</DialogTitle>
          <DialogDescription>{vehicle.model || ""} · {rows.length} cambi registrati</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">{rows.map((c) => <Change key={c.id} c={c} canDelete={isAdmin} onDelete={remove} />)}</div>
      </DialogContent>
    </Dialog>
  );
};
